import { optimizedCompletion } from '@/lib/openai/speedOptimizer';
import { extractContent, parseJsonResponse } from '@/lib/openai/modelRouter';
import { ApiError } from '@/lib/errors';

export interface AutoFillSuggestions {
  company?: string;
  location?: string;
  industry?: string;
  experience?: string;
  experienceLevel?: string;
  jobFunction?: string;
  skills?: string;
  responsibilities?: string;
  qualifications?: string;
  benefits?: string;
  companyMission?: string;
  confidence: number; // 0-100
  reasoning: string;
}

interface AutoFillInput {
  jobTitle?: string;
  company?: string;
  location?: string;
  industry?: string;
  experience?: string;
  experienceLevel?: string;
  jobFunction?: string;
  skills?: string;
  responsibilities?: string;
  qualifications?: string;
  benefits?: string;
  companyMission?: string;
}

export async function getAIAutoFillSuggestions(
  currentData: AutoFillInput,
  triggerField: string
): Promise<AutoFillSuggestions> {
  try {
    // Don't auto-fill if we don't have enough context
    if (!currentData.jobTitle?.trim() && !currentData.company?.trim()) {
      throw new Error('Need at least job title or company name to provide suggestions');
    }

    const prompt = createAutoFillPrompt(currentData, triggerField);
    
    const response = await optimizedCompletion('auto-fill', [
      {
        role: 'system',
        content: `You are an expert HR consultant and job market analyst. Your role is to intelligently auto-fill job description fields based on the information provided.

GUIDELINES:
1. Only suggest fields that are empty or could be improved
2. Base suggestions on industry standards and market knowledge
3. Be specific and realistic, not generic
4. Consider the job title, company, and industry context
5. Provide practical, actionable suggestions
6. Maintain consistency across all fields
7. Use current market trends and standards

RESPONSE FORMAT:
Always respond with valid JSON only, no markdown formatting.
Only include fields that should be auto-filled or improved.
Set confidence based on how certain you are about the suggestions.`
      },
      {
        role: 'user',
        content: prompt
      }
    ], {
      temperature: 0.3,
      max_tokens: 800 // Reduced for faster response
    });

    const content = extractContent(response);
    const suggestions: AutoFillSuggestions = parseJsonResponse(content);
    
    // Validate the response structure
    if (typeof suggestions.confidence !== 'number') {
      suggestions.confidence = 75; // Default confidence
    }
    
    if (!suggestions.reasoning) {
      suggestions.reasoning = 'AI suggestions based on job title and industry standards';
    }
    
    return suggestions;
  } catch (error) {
    console.error('Auto-fill error:', error);
    throw new ApiError(
      error instanceof Error 
        ? `Auto-fill failed: ${error.message}` 
        : 'Failed to generate auto-fill suggestions', 
      500
    );
  }
}

function createAutoFillPrompt(currentData: AutoFillInput, triggerField: string): string {
  const hasJobTitle = currentData.jobTitle?.trim();
  const hasCompany = currentData.company?.trim();
  const hasIndustry = currentData.industry?.trim();
  
  return `Based on the job information provided, suggest intelligent auto-fill values for empty or improvable fields.

CURRENT JOB INFORMATION:
${hasJobTitle ? `Job Title: ${currentData.jobTitle}` : ''}
${hasCompany ? `Company: ${currentData.company}` : ''}
${currentData.location ? `Location: ${currentData.location}` : ''}
${hasIndustry ? `Industry: ${currentData.industry}` : ''}
${currentData.experience ? `Experience: ${currentData.experience}` : ''}
${currentData.experienceLevel ? `Experience Level: ${currentData.experienceLevel}` : ''}
${currentData.jobFunction ? `Job Function: ${currentData.jobFunction}` : ''}
${currentData.skills ? `Skills: ${currentData.skills}` : ''}
${currentData.responsibilities ? `Responsibilities: ${currentData.responsibilities}` : ''}
${currentData.qualifications ? `Qualifications: ${currentData.qualifications}` : ''}
${currentData.benefits ? `Benefits: ${currentData.benefits}` : ''}
${currentData.companyMission ? `Company Mission: ${currentData.companyMission}` : ''}

TRIGGER FIELD: ${triggerField}
(The user just filled in this field, so provide relevant auto-fill suggestions for other empty fields)

ANALYSIS REQUIREMENTS:
1. Suggest realistic industry based on job title and company
2. Recommend appropriate experience level and years
3. Suggest relevant job function category
4. Provide comprehensive skills list based on the role
5. Generate detailed responsibilities aligned with the position
6. Suggest qualifications that match industry standards
7. Recommend competitive benefits for the industry/role
8. Create company mission if company name is provided

Respond with JSON in this exact format:
{
  "industry": "<industry suggestion if empty>",
  "experience": "<years of experience if empty>",
  "experienceLevel": "<Entry Level|Associate|Mid-Level|Senior|Lead|Director|Executive>",
  "jobFunction": "<relevant job function if empty>",
  "skills": "<comprehensive skills list if empty or brief>",
  "responsibilities": "<detailed responsibilities if empty>",
  "qualifications": "<relevant qualifications if empty>",
  "benefits": "<competitive benefits if empty>",
  "companyMission": "<company mission/values if empty and company provided>",
  "confidence": <number 0-100>,
  "reasoning": "<brief explanation of suggestions>"
}

Only include fields that should be auto-filled. Skip fields that are already well-populated.
Be specific and practical, not generic. Use current market knowledge.`;
}

// Smart auto-fill for specific field types
export async function getSmartFieldSuggestion(
  fieldType: 'skills' | 'responsibilities' | 'qualifications' | 'benefits',
  context: {
    jobTitle: string;
    industry?: string;
    experienceLevel?: string;
    company?: string;
  }
): Promise<string> {
  try {
    if (!context.jobTitle?.trim()) {
      throw new Error('Job title is required for smart suggestions');
    }

    const prompt = `As an HR expert, provide a comprehensive ${fieldType} section for this job:

Job Title: ${context.jobTitle}
${context.industry ? `Industry: ${context.industry}` : ''}
${context.experienceLevel ? `Experience Level: ${context.experienceLevel}` : ''}
${context.company ? `Company: ${context.company}` : ''}

Provide a detailed, realistic ${fieldType} section that:
1. Matches the job title and industry standards
2. Is appropriate for the experience level
3. Uses current market trends and requirements
4. Is specific and actionable, not generic

Format as a clean, professional list or paragraph as appropriate for ${fieldType}.
Do not include any markdown formatting or extra text.`;

    const response = await optimizedCompletion('auto-fill', [
      {
        role: 'system',
        content: `You are an expert HR consultant. Provide only the requested ${fieldType} content, formatted cleanly without any extra text or markdown.`
      },
      {
        role: 'user',
        content: prompt
      }
    ], {
      temperature: 0.3, // Reduced for faster response
      max_tokens: 500 // Reduced for faster response
    });

    return extractContent(response).trim();
  } catch (error) {
    console.error(`Smart ${fieldType} suggestion error:`, error);
    throw new ApiError(
      error instanceof Error 
        ? `Failed to generate ${fieldType} suggestions: ${error.message}` 
        : `Failed to generate ${fieldType} suggestions`, 
      500
    );
  }
}

// Quick industry detection based on job title
export function detectIndustryFromJobTitle(jobTitle: string): string | null {
  const jobTitleLower = jobTitle.toLowerCase();
  
  const industryKeywords = {
    'technology': ['developer', 'engineer', 'programmer', 'software', 'data', 'ai', 'ml', 'devops', 'frontend', 'backend', 'fullstack', 'tech'],
    'healthcare': ['nurse', 'doctor', 'medical', 'physician', 'therapist', 'healthcare', 'clinical', 'pharmacy'],
    'finance': ['analyst', 'accountant', 'financial', 'banking', 'investment', 'cfo', 'controller', 'audit'],
    'marketing': ['marketing', 'brand', 'advertising', 'social media', 'content', 'seo', 'digital marketing'],
    'sales': ['sales', 'account executive', 'business development', 'account manager', 'sales representative'],
    'education': ['teacher', 'professor', 'instructor', 'education', 'academic', 'principal', 'dean'],
    'human_resources': ['hr', 'human resources', 'recruiter', 'talent acquisition', 'people operations'],
    'legal': ['lawyer', 'attorney', 'legal', 'counsel', 'paralegal', 'compliance'],
    'consulting': ['consultant', 'advisory', 'strategy', 'management consulting'],
    'design': ['designer', 'ux', 'ui', 'graphic', 'creative', 'art director']
  };
  
  for (const [industry, keywords] of Object.entries(industryKeywords)) {
    if (keywords.some(keyword => jobTitleLower.includes(keyword))) {
      return industry;
    }
  }
  
  return null;
}

// Experience level detection from job title
export function detectExperienceLevelFromJobTitle(jobTitle: string): string | null {
  const jobTitleLower = jobTitle.toLowerCase();
  
  if (jobTitleLower.includes('senior') || jobTitleLower.includes('sr.')) return 'Senior';
  if (jobTitleLower.includes('lead') || jobTitleLower.includes('principal')) return 'Lead';
  if (jobTitleLower.includes('director') || jobTitleLower.includes('head of')) return 'Director';
  if (jobTitleLower.includes('vp') || jobTitleLower.includes('vice president') || jobTitleLower.includes('cto') || jobTitleLower.includes('ceo')) return 'Executive';
  if (jobTitleLower.includes('junior') || jobTitleLower.includes('jr.') || jobTitleLower.includes('entry')) return 'Entry Level';
  if (jobTitleLower.includes('associate')) return 'Associate';
  
  return 'Mid-Level'; // Default assumption
} 